/**
 * Pure H OS · Sprint 12 · R&D mocks — types + live views.
 * Payload now lives in MongoDB `pureh_*` collections.
 */
import { liveArray } from "../services/store";

export type RndPhase = "concept" | "formulation" | "sampling" | "blind" | "approved" | "launched";
export type Family = "Oriental" | "Floral" | "Woody" | "Chypre" | "Fougère" | "Aquatic" | "Gourmand" | "Leather";
export type RndProduct = {
  id: string;
  name: string;
  codename: string;
  brief: string;
  family: Family;
  targetMarket: string;
  leadPerfumer: string;
  phase: RndPhase;
  formulaVersion: string;
  iterations: number;
  costPerLiterEGP: number;
  targetRetailEGP: number;
  ifraCompliant: boolean;
  createdRelative: string;
  hero: string;
  commerceSku?: string;
};
export const RND_PRODUCTS: RndProduct[] = liveArray<RndProduct>("rnd_products");

export type FormulaNoteRow = { component: string; casNumber?: string; dosagePct: number; ifraLimitPct?: number; supplier: string };
export type Formula = {
  id: string;
  productId: string;
  version: string;
  status: "draft" | "trial" | "approved" | "superseded";
  createdRelative: string;
  createdBy: string;
  topNotes: FormulaNoteRow[];
  heartNotes: FormulaNoteRow[];
  baseNotes: FormulaNoteRow[];
  totalPct: number;
  ifraCompliant: boolean;
  costEGPPerLiter: number;
  notes: string;
};
export const FORMULAS: Formula[] = liveArray<Formula>("formulas");

export type MaterialCategory = "Top" | "Heart" | "Base" | "Fixative" | "Solvent" | "Accord";
export type RawMaterial = {
  id: string;
  name: string;
  category: MaterialCategory;
  casNumber: string;
  supplier: string;
  costEGPPerKg: number;
  onHandKg: number;
  ifraLimitPct?: number;
  allergen: boolean;
  origin: string;
};
export const RAW_MATERIALS: RawMaterial[] = liveArray<RawMaterial>("raw_materials");

export type RndSupplier = {
  id: string;
  name: string;
  category: "Fragrance house" | "Specialty" | "Local";
  city: string;
  materials: number;
  leadTimeDays: number;
  paymentTerms: string;
  moqKg: number;
  aiConfidence: number;
};
export const RND_SUPPLIERS: RndSupplier[] = liveArray<RndSupplier>("rnd_suppliers");

export type LabSession = {
  id: string;
  code: string;
  dateRelative: string;
  perfumer: string;
  products: string[];
  outcome: "reformulate" | "advance" | "hold";
  notes: string;
  nextSteps: string;
};
export const LAB_SESSIONS: LabSession[] = liveArray<LabSession>("lab_sessions");

export type SamplingBatch = {
  id: string;
  code: string;
  productId: string;
  version: string;
  units: number;
  sentRelative: string;
  panel: "internal" | "panel-a" | "panel-b" | "panel-c" | "external";
  status: "dispatched" | "in-progress" | "returned" | "complete";
  responsesIn: number;
  responsesTotal: number;
};
export const SAMPLING_BATCHES: SamplingBatch[] = liveArray<SamplingBatch>("sampling_batches");

export type BlindPanelResult = {
  id: string;
  productId: string;
  version: string;
  panel: "A" | "B" | "C";
  panelSize: number;
  responsesIn: number;
  longevity: number;
  sillage: number;
  projection: number;
  wearability: number;
  uniqueness: number;
  memorability: number;
  overall: number;
  recommendation: "advance" | "reformulate" | "kill";
  topComment: string;
};
export const BLIND_PANELS: BlindPanelResult[] = liveArray<BlindPanelResult>("blind_panels");

export type RndDecision = {
  id: string;
  productId: string;
  dateRelative: string;
  decidedBy: string;
  decision: "go" | "reformulate" | "hold" | "kill";
  budgetImpactEGP: number;
  rationale: string;
};
export const RND_DECISIONS: RndDecision[] = liveArray<RndDecision>("rnd_decisions");

export type RndTimelineEvent = {
  id: string;
  productId: string;
  kind: "concept" | "formula" | "lab" | "sampling" | "panel" | "decision" | "launch";
  title: string;
  detail: string;
  dateRelative: string;
};
export const RND_TIMELINE: RndTimelineEvent[] = liveArray<RndTimelineEvent>("rnd_timeline");
